/**
 * Multi-asset selection state extracted from `assetsShell.tsx`.
 *
 * Exposes `<AssetsSelectionProvider>` and `useAssetsSelection()` so the
 * selection state can live above `PanelShell` without `assetsShell.tsx`
 * needing to know about `SlideLayout` internals.
 */
import {
  cacheKeys,
  computeCommonGroups,
  computeGroupChanges,
  Group,
  UPDATE_ASSETS_GROUPS,
  useAllWorkspaceGroups,
  useCreateGroup,
  useCurrentWorkspace,
  useCustomMutation,
} from '@gripp/shared-logic';
import { SelectionStatus } from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { SelectedAssetRow } from './assetComponent';

export interface AssetsSelectionState {
  rowsSelected: SelectedAssetRow[];
  setRowsSelected: (rows: SelectedAssetRow[]) => void;
  rowsSelectedIds: string[];
  selectedGroups: Group[];
  selectionStatus: SelectionStatus;
  setSelectionStatus: (status: SelectionStatus) => void;
  hasSelection: boolean;
  clearSelection: () => void;
  handleGroupsChange: (nextGroups: Group[]) => Promise<void>;
  currentWorkspaceId: string | undefined;
  createGroup: ReturnType<typeof useCreateGroup>['createGroup'];
}

const AssetsSelectionContext = createContext<AssetsSelectionState | null>(null);

export const useAssetsSelection = (): AssetsSelectionState => {
  const ctx = useContext(AssetsSelectionContext);
  if (!ctx) {
    throw new Error('useAssetsSelection must be used within AssetsSelectionProvider');
  }
  return ctx;
};

export const AssetsSelectionProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const location = useLocation();
  const queryClient = useQueryClient();
  const { createGroup } = useCreateGroup();
  const workspace = useCurrentWorkspace(true);
  const currentWorkspaceId = workspace?.id;

  const { groups: availableGroups } = useAllWorkspaceGroups(currentWorkspaceId);

  const [rowsSelected, setRowsSelected] = useState<SelectedAssetRow[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<Group[]>([]);
  const [selectionStatus, setSelectionStatus] = useState<SelectionStatus>('idle');

  const rowsSelectedIds = useMemo(
    () => rowsSelected.map((r) => r.id),
    [rowsSelected]
  );

  const hasSelection = rowsSelected.length > 0;

  const { mutateAsync: updateAssetsGroups } = useCustomMutation({
    query: UPDATE_ASSETS_GROUPS,
  });

  useEffect(() => {
    if (!hasSelection) {
      setSelectedGroups([]);
      return;
    }

    const nextSelected = computeCommonGroups(
      rowsSelected,
      (row) => row.groups ?? [],
      availableGroups
    ).map((g) => ({ id: g.id, name: g.name }));

    setSelectedGroups(nextSelected);
  }, [rowsSelected, availableGroups, hasSelection]);

  const clearSelection = useCallback(() => {
    setRowsSelected([]);
    setSelectionStatus('reset');
  }, []);

  // Drop the selection on any sub-route change so the navigated panel becomes
  // visible. Skip the initial render so first mounting at any path doesn't
  // fire a no-op clear (which would still re-broadcast selectionStatus:'reset').
  const prevPathnameRef = useRef(location.pathname);
  useLayoutEffect(() => {
    if (prevPathnameRef.current === location.pathname) return;
    prevPathnameRef.current = location.pathname;
    clearSelection();
  }, [location.pathname, clearSelection]);

  const handleGroupsChange = useCallback(
    async (nextGroups: Group[]) => {
      const { added, removed } = computeGroupChanges(selectedGroups, nextGroups);

      setSelectedGroups(nextGroups);

      if (!currentWorkspaceId) {
        return;
      }

      await updateAssetsGroups({
        input: {
          addedGroups: added.map((g) => ({ id: g.id })),
          removedGroups: removed.map((g) => ({ id: g.id })),
          assetIds: rowsSelectedIds,
          workspaceId: currentWorkspaceId,
        },
      });

      await queryClient.invalidateQueries({
        queryKey: cacheKeys('asset').all,
      });
    },
    [
      selectedGroups,
      rowsSelectedIds,
      currentWorkspaceId,
      updateAssetsGroups,
      queryClient,
    ]
  );

  const value = useMemo<AssetsSelectionState>(
    () => ({
      rowsSelected,
      setRowsSelected,
      rowsSelectedIds,
      selectedGroups,
      selectionStatus,
      setSelectionStatus,
      hasSelection,
      clearSelection,
      handleGroupsChange,
      currentWorkspaceId,
      createGroup,
    }),
    [
      rowsSelected,
      rowsSelectedIds,
      selectedGroups,
      selectionStatus,
      hasSelection,
      clearSelection,
      handleGroupsChange,
      currentWorkspaceId,
      createGroup,
    ]
  );

  return (
    <AssetsSelectionContext.Provider value={value}>
      {children}
    </AssetsSelectionContext.Provider>
  );
};
